import React, { useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import {
  NAME_SEARCH_DEBOUNCE_MS,
  NAME_SEARCH_MAX_RESULTS,
} from '../../constants/app.constants';
import { searchProductsByName, type SkuNameItem } from '../../services/SKUListAPI';
import { renderSkuWithSoftBreaks } from '../../utils/skuSoftBreak';
import ProductImage from './ProductImage';
import './ProductNameSearch.css';

interface ProductNameSearchModalProps {
  open: boolean;
  initialQuery?: string;
  title?: string;
  matchSku?: boolean;
  showThumbnails?: boolean;
  onClose: () => void;
  onSelect: (item: SkuNameItem) => void;
  footer?: React.ReactNode;
}

function ProductNameSearchModal({
  open,
  initialQuery = '',
  title = 'Search by Name',
  matchSku = false,
  showThumbnails = false,
  onClose,
  onSelect,
  footer,
}: ProductNameSearchModalProps): React.ReactElement | null {
  const [manualNameQuery, setManualNameQuery] = useState(initialQuery);
  const [nameSearchResults, setNameSearchResults] = useState<SkuNameItem[]>([]);
  const [nameSearchLoading, setNameSearchLoading] = useState(false);
  const [nameSelectedIndex, setNameSelectedIndex] = useState(-1);
  const [showNameAutocomplete, setShowNameAutocomplete] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const nameAutocompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setManualNameQuery(initialQuery);
    setNameSearchResults([]);
    setShowNameAutocomplete(false);
    setNameSelectedIndex(-1);
    setNameSearchLoading(false);
    const timer = window.setTimeout(() => {
      nameInputRef.current?.focus();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return undefined;
    const trimmed = manualNameQuery.trim();
    if (!trimmed) {
      setNameSearchResults([]);
      setNameSearchLoading(false);
      setShowNameAutocomplete(false);
      return undefined;
    }

    let cancelled = false;
    setNameSearchLoading(true);
    const timer = window.setTimeout(async () => {
      const results = await searchProductsByName(trimmed, NAME_SEARCH_MAX_RESULTS, matchSku);
      if (cancelled) return;
      setNameSearchResults(results);
      setNameSearchLoading(false);
      setShowNameAutocomplete(results.length > 0);
      setNameSelectedIndex(-1);
    }, NAME_SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [manualNameQuery, matchSku, open]);

  useEffect(() => {
    if (showNameAutocomplete && nameInputRef.current && nameAutocompleteRef.current) {
      const inputRect = nameInputRef.current.getBoundingClientRect();
      const dropdown = nameAutocompleteRef.current;
      dropdown.style.top = `${inputRect.bottom}px`;
      dropdown.style.left = `${inputRect.left}px`;
      dropdown.style.width = `${inputRect.width}px`;
    }
  }, [showNameAutocomplete, manualNameQuery, nameSearchResults]);

  useClickOutside(
    [nameAutocompleteRef, nameInputRef],
    () => setShowNameAutocomplete(false),
  );

  if (!open) {
    return null;
  }

  const selectResult = (item: SkuNameItem): void => {
    setShowNameAutocomplete(false);
    setNameSelectedIndex(-1);
    onSelect(item);
  };

  const handleNameInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showNameAutocomplete || nameSearchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setNameSelectedIndex((prev) => (prev < nameSearchResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setNameSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && nameSelectedIndex >= 0) {
      e.preventDefault();
      selectResult(nameSearchResults[nameSelectedIndex]);
    } else if (e.key === 'Escape') {
      setShowNameAutocomplete(false);
    }
  };

  return (
    <div
      className="pbl-modal-overlay"
      onClick={(event) => {
        event.stopPropagation();
        onClose();
      }}
    >
      <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pbl-modal-close" onClick={onClose}>
          ×
        </button>
        <div className="pbl-modal-title">{title}</div>
        <div className="pbl-manual-popup">
          <div className="pbl-sku-input-wrapper">
            <input
              ref={nameInputRef}
              type="text"
              value={manualNameQuery}
              onChange={(e) => setManualNameQuery(e.target.value)}
              onKeyDown={handleNameInputKeyDown}
              placeholder={matchSku ? 'e.g. suspension ring or RSV-' : 'e.g. suspension ring'}
              className="pbl-sku-input"
            />
            {showNameAutocomplete && nameSearchResults.length > 0 && (
              <div ref={nameAutocompleteRef} className="pbl-autocomplete-dropdown">
                {nameSearchResults.map((item, index) => (
                  <div
                    key={`${item.sku}-${index}`}
                    className={`pbl-autocomplete-item sw-name-result-item${showThumbnails ? ' sw-name-result-item-with-thumb' : ''} ${index === nameSelectedIndex ? 'selected' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      selectResult(item);
                    }}
                    onMouseEnter={() => setNameSelectedIndex(index)}
                  >
                    {showThumbnails ? (
                      <div className="sw-name-result-thumb">
                        <ProductImage sku={item.sku} alt={item.name} />
                      </div>
                    ) : null}
                    <div className="sw-name-result-copy">
                      <div className="sw-name-result-name">{item.name}</div>
                      <div className="sw-name-result-sku">{renderSkuWithSoftBreaks(item.sku)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {nameSearchLoading && (
            <div className="sw-name-search-status">Searching…</div>
          )}
          {!nameSearchLoading && manualNameQuery.trim() && nameSearchResults.length === 0 && (
            <div className="sw-name-search-status">No matching products</div>
          )}
        </div>
        {footer}
      </div>
    </div>
  );
}

export default ProductNameSearchModal;
